import React, { useEffect, useState } from 'react'
import { createUseStyles } from 'react-jss'
import { useAtom } from 'jotai'
import { useSearchParams } from 'react-router-dom'
import SwiperCore, { Navigation, Pagination } from 'swiper/core'
import { container, headerArea } from '../../../theme/reuseables'
import { Typography } from '../../ui/Typography/Typography'
import { colors } from '../../../theme/palette'
import breakpoints from '../../../theme/breakpoints'

// Import Swiper styles
import 'swiper/swiper-bundle.css'
import { L, L_HTML } from '../../providers/Lokalise'
import Carousel from './Carousel'
import OpenCases from './OpenCases'
import ClosedCases from './ClosedCases'
import {
    openCasesAtom,
    casesToConfirmAtom,
    hasTestCasesAtom,
} from '../../../store/filter/filterCasesAtom'
import Switch from '../../ui/Switch'
import { Help } from '../../../assets/icons'
import { Tooltip } from '../../ui/Tooltip/Tooltip'
import { HeaderLinks } from '../../modules/Headerlinks'

// Initialize Swiper modules
SwiperCore.use([Navigation, Pagination])

const useStyles = createUseStyles({
    container: {
        ...container,
        [`@media (max-width: ${breakpoints.Desktop}px)`]: {
            padding: '0',
        },
    },
    headerArea: {
        ...headerArea,
        [`@media (max-width: ${breakpoints.Desktop}px)`]: {
            padding: '0',
            minHeight: '30px',
        },
    },
    headerTitle: {
        padding: '1rem 0',
        [`@media (min-width: ${breakpoints.Desktop}px)`]: {
            padding: '3rem 0',
        },
    },
    headerActions: {
        display: 'flex',
        flexGrow: 1,
        marginLeft: 'auto',
        justifyContent: 'flex-end',
        alignItems: 'flex-start',
        flexShrink: 0,
    },
    headerAreaTop: {
        display: 'flex',
        padding: '1rem 0 .5rem',
        borderBottom: `1px solid ${colors.borderColor}`,
        marginBottom: '1rem',
    },
    tabs: {
        display: 'flex',
        borderBottom: `2px solid ${colors.borderColorLight}`,
        marginBottom: '1rem',
    },
    tab: {
        padding: '0.5rem 2rem',
        width: '15.75rem',
        cursor: 'pointer',
        border: '2px solid transparent',
        backgroundColor: 'transparent',
        borderBottom: '2px solid transparent',
        position: 'relative',
        '&.active': {
            color: colors.green,
            fontWeight: 700,
            '&:after': {
                content: '""',
                position: 'absolute',
                bottom: '-4px',
                left: '0',
                width: '100%',
                height: '2px',
                backgroundColor: colors.green,
            },
        },
        [`@media (max-width: ${breakpoints.Desktop}px)`]: {
            padding: '0.5rem 1rem',
            fontSize: '0.875rem',
        },
    },
    tabContent: {
        padding: '1rem 0',
        position: 'relative',
        overflow: 'visible',
        '& .swiper': {
            overflow: 'visible',
            paddingBottom: '2rem',
            position: 'relative',
        },
        '& .swiper-slide': {
            width: 'auto',
        },
        '& .pagination': {
            bottom: '0',
            '& .swiper-pagination-bullet': {
                width: '10px',
                height: '10px',
                backgroundColor: colors.gray900,
                opacity: 0.4,
                '&.swiper-pagination-bullet-active': {
                    opacity: 1,
                },
            },
        },
        '& .swiper-button-next, & .swiper-button-prev': {
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '30px',
            height: '30px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 0,
            margin: '0',

            '& svg': {
                width: '15px',
                height: '24px',
                fill: colors.green600,
                color: colors.green600,
            },
            '&:hover': {
                backgroundColor: colors.gray500,
            },
            '&.swiper-button-disabled': {
                opacity: 0.35,
                cursor: 'auto',
                pointerEvents: 'none',
            },
            '&::after': {
                display: 'none',
            },
        },
        '& .swiper-button-prev': {
            left: '-30px',
        },
        '& .swiper-button-next': {
            right: '-30px',
        },
    },
    switchContainer: {
        display: 'flex',
        gap: '0.5rem',
        alignItems: 'center',
        justifyContent: 'center',
    },
    switchIcon: {
        borderRadius: '50%',
        border: `1.5px solid ${colors.green}`,
        padding: '0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '18px',
        height: '18px',
    },
    switchLabel: {
        display: 'flex',
        gap: '0.5rem',
        alignItems: 'center',
        justifyContent: 'center',
    },
    toggleContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        [`@media (max-width: ${breakpoints.Desktop}px)`]: {
            marginTop: '0.5rem',
            justifyContent: 'flex-start',
        },
    },
})

const Salary: React.FC = () => {
    const classes = useStyles()
    const [activeTab, setActiveTab] = useState(0)
    const [casesToConfirm] = useAtom(casesToConfirmAtom)
    const [openCases] = useAtom(openCasesAtom)
    const [searchParams, setSearchParams] = useSearchParams()
    const [hasTestCases] = useAtom(hasTestCasesAtom)
    const [isChecked, setIsChecked] = useState(() => {
        const saved = localStorage.getItem('salary-show-test-cases')
        return saved ? JSON.parse(saved) : false
    })

    const tabs = [
        {
            label: L('LØN_FANE_1'),
            content: L('LØN_FANE_1'),
        },
        {
            label: L('LØN_FANE_2'),
            content: L('LØN_FANE_2'),
        },
    ]

    useEffect(() => {
        const tabParam = searchParams.get('tab')
        if (tabParam === 'aabne') {
            setActiveTab(0)
        } else if (tabParam === 'lukket') {
            setActiveTab(1)
        } else {
            // Default to tab 0 if no param or invalid param
            setActiveTab(0)
        }
    }, [searchParams])

    if (process.env.REACT_APP_SALARY_IS_ACTIVE !== 'true') {
        return null
    }

    return (
        <div className={`${classes.container} page-up`}>
            <div className={`${classes.headerAreaTop} desktop-only`}>
                <div className={classes.headerActions}>
                    <HeaderLinks showGotoMembrsearchModal={false} />
                </div>
            </div>
            <div className={classes.headerArea}>
                <div className={classes.headerTitle}>
                    <Typography tag="h1" size="XXXXL" mobileSize="L">
                        {L_HTML('MINELØNINDPLACERINGER_TITEL')}
                    </Typography>
                </div>
            </div>
            <div>
                <div className={classes.tabs}>
                    {tabs.map((tab, index) => (
                        <button
                            type="button"
                            key={tab.label}
                            className={`${classes.tab} ${
                                activeTab === index ? 'active' : ''
                            }`}
                            onClick={() => {
                                setActiveTab(index)
                                const newParams = new URLSearchParams(
                                    searchParams
                                )
                                newParams.set(
                                    'tab',
                                    index === 0 ? 'aabne' : 'lukket'
                                )
                                setSearchParams(newParams)
                            }}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
                <div className={classes.tabContent}>
                    {activeTab === 0 ? (
                        <div className={classes.tabContent}>
                            <div style={{ marginBottom: '2rem' }}>
                                <div
                                    style={{
                                        paddingBottom: '1rem',
                                        borderBottom: `1px solid ${colors.borderColorLight}`,
                                        marginBottom: '1rem',
                                    }}
                                >
                                    <Typography
                                        tag="h2"
                                        size="S"
                                        style={{ fontWeight: 600 }}
                                    >
                                        {`${L(
                                            'LØN_SAGER_IKKE_TAGET_TITLE'
                                        )} (${String(
                                            casesToConfirm.filter(
                                                (caseItem) =>
                                                    isChecked ||
                                                    caseItem.caseNumber?.startsWith(
                                                        'LI'
                                                    )
                                            ).length
                                        )})`}
                                    </Typography>
                                    <div className={classes.toggleContainer}>
                                        {hasTestCases && (
                                            <div>
                                                <Tooltip
                                                    content={L(
                                                        'LØN_TOGGLE_TEST_SAGER_ON_OFF_TOOLTIP'
                                                    )}
                                                >
                                                    <div
                                                        className={
                                                            classes.switchContainer
                                                        }
                                                    >
                                                        <div
                                                            className={
                                                                classes.switchLabel
                                                            }
                                                        >
                                                            <Typography size="XS">
                                                                {L(
                                                                    'LØN_TOGGLE_TEST_SAGER_ON_OFF_LABEL'
                                                                )}
                                                            </Typography>
                                                            <div>
                                                                <Help
                                                                    className={
                                                                        classes.switchIcon
                                                                    }
                                                                    fill={
                                                                        colors.green
                                                                    }
                                                                />
                                                            </div>
                                                        </div>
                                                        <Switch.Component
                                                            id="toggle-test-cases"
                                                            label=""
                                                            checked={isChecked}
                                                            onCheckedChange={() => {
                                                                const newValue =
                                                                    !isChecked
                                                                setIsChecked(
                                                                    newValue
                                                                )
                                                                localStorage.setItem(
                                                                    'salary-show-test-cases',
                                                                    JSON.stringify(
                                                                        newValue
                                                                    )
                                                                )
                                                            }}
                                                        />
                                                    </div>
                                                </Tooltip>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <Carousel showTestCases={isChecked} />
                            </div>
                            <div>
                                <div
                                    style={{
                                        paddingBottom: '1rem',
                                        borderBottom: `1px solid ${colors.borderColorLight}`,
                                        marginBottom: '1rem',
                                    }}
                                >
                                    <Typography
                                        tag="h2"
                                        size="S"
                                        style={{ fontWeight: 600 }}
                                    >
                                        {`${L(
                                            'LØN_ÅBNE_SAGER_TITLE'
                                        )} (${String(
                                            openCases.filter(
                                                (caseItem) =>
                                                    isChecked ||
                                                    caseItem.caseNumber?.startsWith(
                                                        'LI'
                                                    )
                                            ).length
                                        )})`}
                                    </Typography>
                                </div>
                                <OpenCases showTestCases={isChecked} />
                            </div>
                        </div>
                    ) : (
                        <div>
                            <ClosedCases cases={casesToConfirm} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export { Salary }
